/**
 * Manual 5D cost entries, stored the way the MIS module stores its own data:
 * free-form JSON inside the plant's MIS record (GET/POST /mis/plant/:id, read
 * through getPlantMis and written through upsertPlantMis with the rest of the
 * record merged back in - see MISContext.saveDeviationsToApi). They live under
 * a new `fiveD` key, per PV project and revision:
 *
 *   fiveD[projectId][revision] = { entries: [ {id, type, code, cat, date,
 *                                              amount, note, source} ],
 *                                   switchPolicy: {...} }
 *
 * `switchPolicy` (the switch model's client/vendor payment-lag settings, see
 * switchLink.js) lives alongside `entries` in that same per-revision object -
 * setLedgerInMis and switchLink.js's setSwitchPolicyInMis both merge into it
 * rather than replace it, so saving one never drops the other.
 *
 * Each entry is one dated amount, the shape MIS invoices already use
 * (invN = { date, amount }):
 *   type    bud | act | inv | pd     (budget / actual / invoiced / paid)
 *   code    stable internal line code ("2.1") - for bud / act / pd
 *   cat     category key ("1".."4")  - for inv, since invoices are per
 *           discipline, not per line (derived from `code` for the others)
 *   source  "manual" | "sample"      (sample entries can be removed in one go)
 *
 * These ADD to what the page already reads from POs, payments and MIS
 * invoices - they are for what those modules don't carry (per-line budget,
 * costs not yet in a PO, ...) and for demos.
 */

import { CATEGORIES } from "./wbsConfig";
import { catKeyOf, estCost } from "./buildDashboardLines";

export const LEDGER_TYPES = [
  { value: "bud", label: "Budget" },
  { value: "act", label: "Actual" },
  { value: "inv", label: "Invoiced" },
  { value: "pd", label: "Paid" },
];

const TYPE_VALUES = LEDGER_TYPES.map((t) => t.value);

export const newEntryId = () =>
  `e-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const num = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const isoDate = (value) => {
  const s = String(value ?? "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : "";
};

// Same unwrap tolerance as extractInvoices: the MIS payload is nested under
// data.data, but a couple of shallower shapes are accepted too. Exported for
// switchLink.js's policy storage, which lives in this same fiveD structure.
export const misRoot = (mis) =>
  [mis?.data?.data, mis?.data, mis].find(
    (root) => root && typeof root === "object" && root.fiveD,
  ) || null;

/** Entries saved for this project + revision, normalised. */
export const extractLedger = (mis, projectId, revision) => {
  const raw = misRoot(mis)?.fiveD?.[projectId]?.[revision]?.entries;
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => ({
      id: String(entry?.id ?? newEntryId()),
      type: TYPE_VALUES.includes(entry?.type) ? entry.type : null,
      code: entry?.code ? String(entry.code) : null,
      cat: entry?.cat ? String(entry.cat) : null,
      date: isoDate(entry?.date),
      amount: num(entry?.amount),
      note: String(entry?.note ?? ""),
      source: entry?.source === "sample" ? "sample" : "manual",
    }))
    .filter((entry) => entry.type && entry.amount !== null);
};

/**
 * The MIS JSON to POST after this project + revision's entries change: the
 * existing record (`mis.data.data`, as MISContext reads it) with only `fiveD`
 * touched, so invoices, deviations and other projects survive. Merges into
 * the per-revision object rather than replacing it, so a switchPolicy already
 * saved there (switchLink.js) isn't dropped.
 */
export const setLedgerInMis = (existing, projectId, revision, entries) => {
  const base = existing && typeof existing === "object" ? existing : {};
  const fiveD = base.fiveD && typeof base.fiveD === "object" ? base.fiveD : {};
  const project =
    fiveD[projectId] && typeof fiveD[projectId] === "object"
      ? fiveD[projectId]
      : {};
  const revisionBlob =
    project[revision] && typeof project[revision] === "object"
      ? project[revision]
      : {};

  return {
    ...base,
    fiveD: {
      ...fiveD,
      [projectId]: { ...project, [revision]: { ...revisionBlob, entries } },
    },
  };
};

/** What can be saved: a type, an amount, a date, and a line (or category for invoices). */
export const validateEntry = (entry) => {
  if (!TYPE_VALUES.includes(entry.type)) return "type";
  if (num(entry.amount) === null) return "amount";
  if (!isoDate(entry.date)) return "date";
  if (entry.type === "inv" ? !entry.cat : !entry.code) return "line";
  return null;
};

/**
 * Rolls entries up for the table and totals:
 *   byCode       Map<line code, { bud, act, pd }>  (sums; a key is absent when
 *                that type has no entries, so "no data" stays distinguishable)
 *   invByCat     { [category key]: amount }
 *   events       { bud, act, inv, pd }: [{ date, amount }] for the S-curve
 */
export const summarizeLedger = (entries) => {
  const byCode = new Map();
  const invByCat = {};
  const events = { bud: [], act: [], inv: [], pd: [] };

  entries.forEach((entry) => {
    events[entry.type].push({ date: entry.date, amount: entry.amount });

    if (entry.type === "inv") {
      if (entry.cat) invByCat[entry.cat] = (invByCat[entry.cat] || 0) + entry.amount;
      return;
    }
    if (!entry.code) return;
    const slot = byCode.get(entry.code) || {};
    slot[entry.type] = (slot[entry.type] || 0) + entry.amount;
    byCode.set(entry.code, slot);
  });

  return { byCode, invByCat, events };
};

/** Puts summarised entries onto the lines: budget replaces, actual/paid add. */
export const applyLedger = (lines, summary) => {
  if (!summary || summary.byCode.size === 0) return lines;
  return lines.map((line) => {
    const slot = summary.byCode.get(line.code);
    if (!slot) return line;
    return {
      ...line,
      bcOverride: slot.bud != null ? slot.bud : line.bcOverride,
      ac: slot.act != null ? (line.ac ?? 0) + slot.act : line.ac,
      pc: slot.pd != null ? (line.pc ?? 0) + slot.pd : line.pc,
    };
  });
};

// Months, relative to "now" (0 = this month), each category's work runs over:
// engineering early, procurement next, fabrication later, subcontract last.
// Exported so the demo switch schedule (switchLink.js) tells the same story.
export const SAMPLE_WINDOWS = { 1: [-7, -3], 2: [-6, -1], 3: [-4, 0], 4: [-2, 1] };
const SAMPLE_BUDGET_FACTOR = 0.95; // budget sits a little under the estimate
const SAMPLE_OVERRUN = 1.06; // actuals run a little over budget
const SAMPLE_PAID_SHARE = 0.75; // paid lags actual, and only most of it
const SAMPLE_BILLING_MARKUP = 1.15; // each month's cost is billed at cost + 15%

/**
 * Demo entries shaped like a project part-way through: budget phased over each
 * category's window, actual and paid for months already gone, and invoices per
 * discipline billed alongside that month's cost. Everything is derived from
 * the estimate lines, so the numbers stay proportionate to whatever the
 * revision holds. All tagged "sample".
 */
export const buildSampleEntries = (lines, now = new Date()) => {
  const dateAt = (offset) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 15))
      .toISOString()
      .slice(0, 10);

  const entries = [];
  const push = (type, key, where, date, amount) => {
    const value = Math.round(amount);
    if (value <= 0) return;
    entries.push({
      id: `sample-${type}-${key}-${date}`,
      type,
      code: where.code || null,
      cat: where.cat || null,
      date,
      amount: value,
      note: "Sample data",
      source: "sample",
    });
  };

  // category key -> month offset -> actual cost that month (what gets billed)
  const catActual = {};

  lines.forEach((line) => {
    const est = estCost(line);
    const cat = catKeyOf(line);
    const window = SAMPLE_WINDOWS[cat];
    if (!(est > 0) || !window) return;

    const budget = est * SAMPLE_BUDGET_FACTOR;
    const months = window[1] - window[0] + 1;

    for (let i = 0; i < months; i += 1) {
      const offset = window[0] + i;
      push("bud", line.code, { code: line.code }, dateAt(offset), budget / months);

      if (offset <= -1) {
        const actual = (budget / months) * SAMPLE_OVERRUN;
        catActual[cat] = catActual[cat] || {};
        catActual[cat][offset] = (catActual[cat][offset] || 0) + actual;
        push("act", line.code, { code: line.code }, dateAt(offset), actual);
        push(
          "pd",
          line.code,
          { code: line.code },
          dateAt(offset + 1),
          actual * SAMPLE_PAID_SHARE,
        );
      }
    }
  });

  CATEGORIES.filter((cat) => cat.discipline).forEach((cat) => {
    Object.entries(catActual[cat.k] || {}).forEach(([offset, cost]) =>
      push(
        "inv",
        `cat${cat.k}`,
        { cat: cat.k },
        dateAt(Number(offset)),
        cost * SAMPLE_BILLING_MARKUP,
      ),
    );
  });

  return entries;
};
